import { useState, useEffect, useCallback } from 'react';
import ReactFlow, { 
  Background, 
  Controls, 
  MiniMap, 
  Node,
  Edge,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '../../lib/utils';


interface FlowDiagramProps {
  tenantId?: string;
}

export function FlowDiagram({ tenantId }: FlowDiagramProps) {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadFlowData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get context first
      const contextResponse = await apiClient.get<{ success?: boolean; data?: { context?: { id: string } } }>('/dialplan/context');
      
      const ctxData = contextResponse.data;
      if (!ctxData?.success || !ctxData?.data?.context) {
        throw new Error('Context not found');
      }
      
      const contextId = ctxData.data.context.id;
      
      // Get flow data
      const flowResponse = await apiClient.get<{ success?: boolean; data?: { nodes?: Node[]; edges?: Edge[] } }>(`/dialplan/context/${contextId}/flow`);
      const flowData = flowResponse.data;
      if (flowData?.success) {
        setNodes(flowData.data?.nodes || []);
        setEdges(flowData.data?.edges || []);
      }
    } catch (error) {
      console.error('Error loading flow data:', error);
      toast({
        title: "Error",
        description: getErrorMessage(error) || "Failed to load flow diagram",
        variant: "destructive"
      });
      
      // Set empty state
      setNodes([]);
      setEdges([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadFlowData();
  }, [tenantId, loadFlowData]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <p className="text-lg font-semibold mb-2">No routing configured yet</p>
          <p className="text-sm">Add routes via Call Routing page to see the flow diagram</p>
        </div>
      </div>
    );
  }

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      fitView
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={true}
      attributionPosition="bottom-left"
    >
      <Background />
      <Controls />
      <MiniMap />
      <Panel position="top-right">
        <div className="bg-white p-3 rounded shadow space-y-2 text-xs">
          <div className="font-semibold mb-2">Dialplan Flow</div>
          <div className="text-gray-600">
            Read-only visualization
          </div>
          <div className="flex items-center gap-2 mt-3">
            <div className="w-3 h-3 bg-purple-600 rounded"></div>
            <span>Public Entry</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-indigo-600 rounded"></div>
            <span>Tenant Context</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-600 rounded"></div>
            <span>Routes</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-600 rounded"></div>
            <span>Extensions</span>
          </div>
        </div>
      </Panel>
    </ReactFlow>
  );
}
